// react/jsx-no-constructed-context-values: off;
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { IContext, IProvider, IThemesList, ILayers, ICategory, ITheme, ISubTheme } from './@types/Layers';
import customSubtitles from 'screens/Map/components/ModalLayerOptions/customSubtitles';
import { SEARCH_RESULTS_CATEGORY_ID, serializeActiveLayers, serializeOpenLayers } from 'utils/mapPreferences';
import { captureMonitoringEvent } from 'services/monitoring';

export const LayersContext = createContext({} as IContext);

export function LayersProvider({ children }: IProvider) {
  const [categoryList, setCategoryList] = useState<ICategory[]>([]);
  const [layerList, setLayerList] = useState<ILayers[]>([]);
  const [layerAttributeList, setLayerAttributeList] = useState<ILayers[]>([]);
  const [themesList, setThemesList] = useState<IThemesList[]>([]);
  const [layersActived, setLayersActived] = useState<ILayers[]>([]);
  const [themeOpened, setThemeOpened] = useState<IThemesList>({} as IThemesList);
  const [subThemeOpened, setSubThemeOpened] = useState<ISubTheme>({} as ISubTheme);
  const [subThemeGraph, setSubThemeGraph] = useState<ISubTheme>({} as ISubTheme);
  const [graphCount, setGraphCount] = useState<number>(0);
  const [subtitleLayerOpened, setSubtitleLayerOpened] = useState<ILayers>({} as ILayers);
  const [downloadNotifOpen, setDownloadNotifOpen] = useState<string>('');

  const handleActiveAllThemeLayers = (theme: IThemesList) => {
    const { layers } = theme;

    layers.map(layer => {
      const layerToActive = layersActived.filter(layerActived => layerActived.area_tematica === layer.area_tematica);

      if (layerToActive.length > 0) return;

      void captureMonitoringEvent({
        tipo_evento: 'camada_acessada',
        identificador_camada: layer.layername || String(layer.id),
      });
      return setLayersActived(prevState => [...prevState, layer]); //eslint-disable-line
    });
  };

  const handleOpenThemeLayers = (item: IThemesList) => {
    return setThemeOpened(item);
  };

  const handleSubtitleLayerOpened = (item: ILayers) => {
    return setSubtitleLayerOpened(item);
  };

  const handleRemoveLayer = (layer: ILayers) => {
    const select = document.getElementById(layer.layername);
    select?.click();
    setLayersActived(prev =>
      prev.filter(
        l =>
          l.id !== layer.id ||
          l.layer_filter_id !== layer.layer_filter_id
      )
    );
  };

  const handleClearLayersActived = () => {
    setLayersActived([]);
  };

  const handleAddLayer = (layer: ILayers) => {
    const layerExist = layersActived.find(
      element => element.id === layer.id && (element.layer_filter_id === layer.layer_filter_id ),
    );

    if (!layerExist) {
      layer.subtitle_open = false;
      layer.information_open = false;

      setLayersActived(prevState => [...prevState, layer]);
      void captureMonitoringEvent({
        tipo_evento: 'camada_acessada',
        identificador_camada: layer.layername || String(layer.id),
      });
    }
  };

  const handleEnableOnlyTheseLayers = (layer: ILayers[]) => {
    setLayersActived(layer);
  };

  const handleChangeLayerOptionsOpen = (layer: ILayers, option: string) => {
    const layerIndex = layersActived.findIndex(
      element => element.id === layer.id && (element.layer_filter_id === layer.layer_filter_id),
    );

    if (layerIndex != null) {
      const layers = [...layersActived];
      if (option === 'subtitle') {
        layers[layerIndex].subtitle_open = true;
      } else if (option === 'information') {
        layers[layerIndex].information_open = true;
      }
      setLayersActived(layers);
    }
  };

  const handleActiveCustomSubtitles = (uuid: string) => {
    const layer = layersActived.find(layer => `${layer.layertitle}${layer.layer_filter_id || ''}${layer.layername}` === uuid);
    if (!layer) return;

    const customSubtitle = customSubtitles.find(custom => custom.layername === layer.layername);
    if (customSubtitle && layer) {
      handleChangeLayerOptionsOpen(layer, 'subtitle');
      return;
    }
  };
  
  const handleCloseSubtitles = () => {
    handleCloseOptions('subtitle');
  }

  const handleCloseInformation = () => {
    handleCloseOptions('information');
  }
  
  const handleCloseOptions = (option: string) => {
    const layers = [...layersActived];

    layers.map((layer: ILayers) => {
      if (layer.subtitle_open && option === 'subtitle') layer.subtitle_open = false;
      else if (layer.information_open && option === 'information') layer.information_open = false;
    });

    setLayersActived(layers);
  };

  const handleOpenCategory = (value: boolean, indexCategory: number) => {
    const categories = [...categoryList];
    categories[indexCategory].opened = value;

    if (!value) {
      categories[indexCategory].temas.forEach((theme: ITheme, indexTheme: number) => {
        handleOpenTheme(value, indexCategory, indexTheme);
      });
    }
    setCategoryList(categories);
  };

  const handleOpenTheme = (value: boolean, indexCategory: number, indexTheme: number) => {
    const categories = [...categoryList];
    categories[indexCategory].temas[indexTheme].opened = value;

    const subtema = categories[indexCategory].temas[indexTheme].subtema;
    if (value) {
      handleOpenCategory(true, indexCategory);
    }
    else if (!value && subtema && subtema.length) {
      subtema.forEach((theme: ISubTheme, indexSubTheme: number) => {
        handleOpenSubTheme(value, indexCategory, indexTheme, indexSubTheme);
      });
    }
    setCategoryList(categories);
  };

  const handleOpenSubTheme = (value: boolean, indexCategory: number, indexTheme: number, indexSubThema: number) => {
    const categories = [...categoryList];
    const subtema = categories[indexCategory].temas[indexTheme].subtema;
    if (subtema && subtema?.length) {
      subtema[indexSubThema].opened = value;
    }
    setCategoryList(categories);
    if (subtema) setSubThemeOpened(subtema[indexSubThema]);
  };

  const handleCountLayersActived = () => {
    if (!categoryList.length) return;
    const categories = [...categoryList];

    categories.forEach((category: ICategory) => {
      let countCategory = 0;
      category.temas.forEach((theme: ITheme) => {
        let countTheme = 0;

        if (theme.layers) {
          theme.layers.forEach((layer: ILayers) => {
            if (
              layersActived.find(
                (layerActived: ILayers) =>
                  layerActived.id === layer.id && (layerActived.layer_filter_id === layer.layer_filter_id) && layer.visible,
              )
            ) {
              if (theme.visivel && layer.visible) {
                countCategory = countCategory + 1;
                countTheme = countTheme + 1;
              }
            }

            category.qtdOpenend = countCategory;
            theme.qtdOpenend = countTheme;
          });
        }

        if (!theme.subtema) return;
        theme.subtema.forEach((subTheme: ISubTheme) => {
          let countSubTheme = 0;
          if (!subTheme.layers) return;

          subTheme.layers.forEach((layer: ILayers) => {
            if (
              layersActived.find(
                (layerActived: ILayers) =>
                  layerActived.id === layer.id && (layerActived.layer_filter_id === layer.layer_filter_id) && layer.visible,
              )
            ) {
              if (subTheme.visivel && theme.visivel && layer.visible){
                countCategory = countCategory + 1;
                countTheme = countTheme + 1;
                countSubTheme = countSubTheme + 1;
              }

            }

            category.qtdOpenend = countCategory;
            theme.qtdOpenend = countTheme;
            subTheme.qtdOpenend = countSubTheme;
          });
        });
      });
    });
    setCategoryList(categories);
  };

  const getLayerAttritubeByName = (name: string) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return layerAttributeList.find((layer: ILayers) => layer.layername === name)!;
  };

  const getLayerAttritubeById = (id: number) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return layerAttributeList.find((layer: ILayers) => layer.id === id)!;
  };

  useEffect(() => {
    if (window.__isAuth && !window.__preferencesLoaded) return;
    if (window.__isAuth && !window.__mapPreferencesApplied) return;
    handleCountLayersActived();
    localStorage.setItem('activeLayers', JSON.stringify(serializeActiveLayers(layersActived)));
  }, [layersActived]);

  useEffect(() => {
    if (window.__isAuth && !window.__preferencesLoaded) return;
    if (window.__isAuth && !window.__mapPreferencesApplied) return;
    if (!categoryList.length) return;
    localStorage.setItem('activeCategories', JSON.stringify(serializeOpenLayers(categoryList)));
  }, [categoryList]);

  const handleResetLayersState = useCallback(() => {
    setLayersActived([]);
    setThemeOpened({} as IThemesList);
    setSubThemeOpened({} as ISubTheme);
    setSubThemeGraph({} as ISubTheme);
    setSubtitleLayerOpened({} as ILayers);
    setDownloadNotifOpen('');
    setCategoryList(prev =>
      prev
        .filter(cat => cat.id_categoria !== SEARCH_RESULTS_CATEGORY_ID)
        .map(category => ({
          ...category,
          opened: false,
          qtdOpenend: 0,
          temas: category.temas.map(theme => ({
            ...theme,
            opened: false,
            qtdOpenend: 0,
            subtema: theme.subtema?.map(subtheme => ({
              ...subtheme,
              opened: false,
              qtdOpenend: 0,
            })),
          })),
        }))
    );
  }, []);

  useEffect(() => {
    const handleClearDisplay = () => {
      handleResetLayersState();
    };

    window.addEventListener('p3m:clearMapDisplay', handleClearDisplay);
    return () => {
      window.removeEventListener('p3m:clearMapDisplay', handleClearDisplay);
    };
  }, [handleResetLayersState]);

  return (
    <LayersContext.Provider
      value={{
        categoryList,
        setCategoryList,
        layerList,
        setLayerList,
        layerAttributeList,
        setLayerAttributeList,
        themesList,
        setThemesList,
        layersActived,
        themeOpened,
        subThemeOpened,
        subThemeGraph,
        graphCount,
        setGraphCount,
        downloadNotifOpen,
        setDownloadNotifOpen,
        subtitleLayerOpened,
        handleActiveAllThemeLayers,
        handleAddLayer,
        handleRemoveLayer,
        handleClearLayersActived,
        handleOpenThemeLayers,
        handleSubtitleLayerOpened,
        handleEnableOnlyTheseLayers,
        handleChangeLayerOptionsOpen,
        handleCloseSubtitles,
        handleCloseInformation,
        handleOpenCategory,
        handleOpenTheme,
        handleOpenSubTheme,
        handleCountLayersActived,
        setSubThemeGraph,
        getLayerAttritubeByName,
        getLayerAttritubeById,
        handleActiveCustomSubtitles,
        handleResetLayersState,
      }}
    >
      {children}
    </LayersContext.Provider>
  );
}
